import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Chip, Tooltip, Avatar, Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import dayjs from "dayjs";

// Get initials from name for avatar
const getInitials = (name) => {
  if (!name || name === "-") return "?";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

// Get color based on name for avatar
const getAvatarColor = (name) => {
  const colors = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2", "#c2185b", "#0097a7", "#5d4037"];
  if (!name) return colors[0];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const OverflowTip = ({ children }) => {
  const [isOverflowed, setIsOverflow] = useState(false);
  const textElementRef = useRef();
  useEffect(() => {
    setIsOverflow(textElementRef.current.scrollWidth > textElementRef.current.clientWidth);
  }, []);
  return (
    <Tooltip
      componentsProps={{ tooltip: { sx: { backgroundColor: "#F5F5F5" } } }}
      title={<div className="overflow-tooltip-hover">{children}</div>}
      disableHoverListener={!isOverflowed}
      disableInteractive
    >
      <div ref={textElementRef} className="overflow-tooltip">
        {children}
      </div>
    </Tooltip>
  );
};
export const USERS_COLUMNS = (setState, allowUpdateUser) => [
  {
    field: "name",
    headerName: "User",
    sortable: true,
    minWidth: 220,
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: getAvatarColor(params.row.name),
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {getInitials(params.row.name)}
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EmailIcon sx={{ fontSize: 12 }} />
            {params.row.email}
          </Typography>
        </Box>
      </Box>
    ),
  },
  {
    field: "mobileNumber",
    headerName: "Mobile",
    sortable: true,
    width: 130,
    renderCell: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="body2">{params.value || "-"}</Typography>
      </Box>
    ),
  },
  {
    field: "createdTime",
    headerName: "Created",
    sortable: true,
    minWidth: 120,
    type: "date",
    valueGetter: (params) => {
      if (params.value && params.value > 0) {
        return dayjs(params.value).format("DD/MM/YY");
      } else {
        return "-";
      }
    },
    renderCell: (params) => {
      if (params.row.createdTime && params.row.createdTime > 0) {
        return (
          <Tooltip title={dayjs(params.row.createdTime).format("DD/MM/YYYY HH:mm:ss")}>
            <Typography variant="body2">{dayjs(params.row.createdTime).format("DD/MM/YY")}</Typography>
          </Tooltip>
        );
      }
      return "-";
    },
  },
  {
    field: "stations",
    headerName: "Stations",
    //     headerAlign: "center",
    //   align: "center",
    sortable: true,
    flex: 2,
    width: 180,
    valueGetter: (params) => {
      return `${[...(params.value ?? [])]?.sort() ?? []}`;
    },
    renderCell: (params) => {
      const _sortedValues = [...(params.row?.stations || [])]?.sort();
      const _id = params.row.id;
      const stations = _sortedValues?.map((station, index) => (
        <Chip
          className="users__table__station-chip"
          key={"station" + _id + index}
          label={station}
          size="small"
          variant="filled"
        />
      ));
      return <OverflowTip>{stations}</OverflowTip>;
    },
  },
  {
    field: "isEnable",
    headerName: "Status",
    sortable: true,
    flex: 1,
    width: 80,
    renderCell: (params) => (
      <Chip
        label={params.row?.isEnable ? "Active" : "Inactive"}
        color={params.row?.isEnable ? "primary" : "error"}
        size="small"
        variant="filled"
      />
    ),
    filterable: false,
    valueGetter: (params) => {
      return params.row?.isEnable ? "Active" : "Inactive";
    },
  },
  {
    field: "G/NG",
    headerName: "Service Type",
    //     headerAlign: "center",
    //   align: "center",
    sortable: true,
    width: 120,
  },
  {
    field: "authority",
    headerName: "Role",
    //     headerAlign: "center",
    //   align: "center",
    sortable: true,
    width: 120,
  },
  {
    field: "softwareResponses",
    headerName: "Systems",
    //     headerAlign: "center",
    //   align: "center",
    sortable: true,
    width: 120,
    valueGetter: (params) => {
      return `${[...(params.value ?? [])]?.sort().map((system) => system.code) ?? []}`;
    },
    renderCell: (params) => { 
      const _sortedValues = params.row.softwareResponses;
      const _id = params.row.id;
      const systems = _sortedValues?.map((system, index) => (
        <Chip
          className="users__table__station-chip"
          key={"system" + _id + index}
          label={system.code}
          size="small"
          variant="filled"
        />
      ));
      return <OverflowTip>{systems}</OverflowTip>;
    },
  },
  {
    field: "editActions",
    type: "actions",
    width: 80,
    hideable: false,
    getActions: (params) =>
      allowUpdateUser
        ? [
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => {
                setState((previousState) => {
                  previousState.selectedRow = params.row;
                  previousState.userActionLabel = "Edit User";
                  previousState.showUserModal = true;
                });
              }}
            />,
            // <GridActionsCellItem
            //   icon={<DeleteIcon />}
            //   label="Delete"
            //   onClick={() => {
            //     setState((previousState) => {
            //       previousState.showDeleteModal = true;
            //       previousState.id = params.id;
            //     });
            //   }}
            // />,
            <GridActionsCellItem
            icon={params.row.isEnable ? <PersonOffIcon /> : <CheckCircleIcon />}
            label={params.row.isEnable ? "Deactivate User" : "Activate User"}
            title={params.row.isEnable ? "Deactivate User" : "Activate User"}
            onClick={() => {
              setState((previousState) => {
                previousState.showDeactivateModal = true;
                previousState.id = params.id;
                previousState.selectedRow = params.row;
              });
            }}
          />,
          ]
        : [],
  },
];
